import { createContext, useContext, useState, useEffect } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase/config'
import { useAuth } from './AuthContext'

interface SocialContextType {
  pendingFriendRequestCount: number
  unreadMessageCount: number
}

const SocialContext = createContext<SocialContextType>({
  pendingFriendRequestCount: 0,
  unreadMessageCount: 0,
})

export const useSocial = () => useContext(SocialContext)

export const SocialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth()
  const [pendingFriendRequestCount, setPendingFriendRequestCount] = useState(0)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)

  useEffect(() => {
    if (!currentUser) {
      setPendingFriendRequestCount(0)
      setUnreadMessageCount(0)
      return
    }

    // Single onSnapshot on user doc for badge counts
    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data()
          setPendingFriendRequestCount(data.pendingFriendRequestCount || 0)
          setUnreadMessageCount(data.unreadMessageCount || 0)
        }
      },
      () => {
        // Ignore errors — user doc may not exist yet
      }
    )

    return () => unsub()
  }, [currentUser])

  return (
    <SocialContext.Provider value={{ pendingFriendRequestCount, unreadMessageCount }}>
      {children}
    </SocialContext.Provider>
  )
}
